<template>
  <AppCard
    design="new"
    :title="title"
    title-tag="h3"
    padding="none"
    class="employee-leave-records"
  >
    <template #actions>
      <AppStatusBadge
        design="new"
        tone="neutral"
        compact
        :label="recordCountLabel"
      />
    </template>

    <div v-if="items.length" class="employee-leave-records__table-wrap">
      <table class="employee-leave-records__table" :aria-label="title">
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Period</th>
            <th scope="col" class="employee-leave-records__number">Days</th>
            <th scope="col">Status</th>
            <th scope="col" class="employee-leave-records__note">Note</th>
            <th v-if="allowChanges" scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in items" :key="rowKey(row, index)">
            <td><strong>{{ typeName(row) }}</strong></td>
            <td class="employee-leave-records__period">
              {{ formatDate(row.from_date) }} – {{ formatDate(row.to_date) }}
            </td>
            <td class="employee-leave-records__number">{{ dayCount(row.days) }}</td>
            <td>
              <AppStatusBadge
                design="new"
                compact
                :label="statusLabel(row.status)"
                :tone="statusTone(row.status)"
              />
            </td>
            <td class="employee-leave-records__note">
              <EmployeeLeaveInfoButton
                v-if="noteText(row)"
                :content="noteText(row)"
              />
              <span v-else>—</span>
            </td>
            <td v-if="allowChanges">
              <template v-if="row.leave_id && row.from_date > businessToday() && row.status === 'approved'">
                <AppButton design="new" size="small" @click.native="$emit('mutate', row, 'change')">Change</AppButton>
                <AppButton design="new" size="small" @click.native="$emit('mutate', row, 'cancel')">Cancel leave</AppButton>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <AppNoResults
      v-else
      design="new"
      small
      :title="emptyTitle"
      :text="emptyDescription"
      :margin-top="0"
      :margin-bottom="0"
      class="employee-leave-records__empty"
    />

    <template v-if="collapsible" #footer>
      <AppButton design="new" theme="dark" size="small" @click.native="$emit('toggle')">
        {{ expanded ? 'Show recent records' : `Show all ${count} records` }}
      </AppButton>
    </template>
  </AppCard>
</template>

<script>
import { format, isValid, parseISO } from 'date-fns'
import EmployeeLeaveInfoButton from './EmployeeLeaveInfoButton.vue'
import { businessToday } from '@/views/employees/sales-leave/salesLeave'

const STATUS_LABELS = {
  requested: 'Requested',
  pending: 'Pending',
  approved: 'Approved',
  active: 'Active',
  current: 'Current',
  upcoming: 'Upcoming',
  completed: 'Completed',
  rejected: 'Rejected',
  declined: 'Declined',
  cancelled: 'Cancelled',
}

const STATUS_TONES = {
  requested: 'blue',
  pending: 'blue',
  approved: 'green',
  active: 'green',
  current: 'green',
  upcoming: 'blue',
  completed: 'green',
  rejected: 'red',
  declined: 'red',
  cancelled: 'red',
}

export default {
  name: 'EmployeeLeaveRecordsCard',
  components: {
    EmployeeLeaveInfoButton,
  },
  props: {
    allowChanges: Boolean,
    title: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: null,
    },
    emptyTitle: {
      type: String,
      default: 'No leave records',
    },
    emptyDescription: {
      type: String,
      default: '',
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    count() {
      return this.totalCount === null ? this.items.length : this.totalCount
    },
    recordCountLabel() {
      return `${this.count} ${this.count === 1 ? 'record' : 'records'}`
    },
  },
  methods: {
    businessToday,
    rowKey(row, index) {
      if (row.id !== null && row.id !== undefined) return row.id
      return `${row.from_date}-${row.to_date}-${index}`
    },
    typeName(row) {
      if (row.type && typeof row.type === 'object') return row.type.name || row.type.label || row.type.code || '—'
      return row.type || '—'
    },
    statusCode(status) {
      let value = status
      if (status && typeof status === 'object') value = status.code || status.value || status.name
      return String(value || '').toLowerCase()
    },
    statusLabel(status) {
      if (status && typeof status === 'object' && (status.label || status.name)) {
        return status.label || status.name
      }
      const code = this.statusCode(status)
      return STATUS_LABELS[code] || code || 'Unknown'
    },
    statusTone(status) {
      return STATUS_TONES[this.statusCode(status)] || 'neutral'
    },
    formatDate(value) {
      if (!value) return '—'
      const date = parseISO(value)
      return isValid(date) ? format(date, 'dd MMM yyyy') : value
    },
    dayCount(value) {
      if (value === null || value === undefined || value === '') return '—'
      const days = Number(value)
      return Number.isFinite(days) ? days : '—'
    },
    noteText(row) {
      const notes = []
      if (row.reason) notes.push(`Comment: ${row.reason}`)
      if (row.decision_reason) notes.push(`Decision: ${row.decision_reason}`)
      return notes.join('\n')
    },
  },
}
</script>

<style lang="scss">
.employee-leave-records {
  min-width: 0;
  overflow: hidden;
}

.app-card.app-card--new.employee-leave-records > .app-card__header {
  margin: 0;
  padding: $ui-space-16 $ui-space-16 $ui-space-12;
  border-bottom: 0;
}

.employee-leave-records > .app-card__body {
  min-width: 0;
  padding: 0 $ui-space-16 $ui-space-16;
}

.employee-leave-records .app-card__footer {
  justify-content: center;
  margin: 0;
  padding: $ui-space-16 $ui-space-20;
}

.employee-leave-records__table-wrap {
  min-width: 0;
  overflow-x: auto;
}

.employee-leave-records__table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
  table-layout: fixed;
  color: $ui-text;
  font-family: $ui-font-body;
  font-size: $ui-font-size-body;
}

.employee-leave-records__table th {
  padding: $ui-space-12 $ui-space-16;
  border-bottom: 1px solid $ui-line;
  background-color: $ui-surface-soft;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
}

.employee-leave-records__table td {
  padding: $ui-space-12 $ui-space-16;
  border-bottom: 1px solid $ui-line;
  line-height: 1.45;
  vertical-align: middle;
  overflow-wrap: anywhere;
}

.employee-leave-records__table tbody tr:last-child td {
  border-bottom: 0;
}

.employee-leave-records__table strong {
  color: $ui-ink;
  font-weight: 600;
}

.employee-leave-records__table th:nth-child(1) { width: 22%; }
.employee-leave-records__table th:nth-child(2) { width: 32%; }
.employee-leave-records__table th:nth-child(3) { width: 10%; }
.employee-leave-records__table th:nth-child(4) { width: 25%; }
.employee-leave-records__table th:nth-child(5) { width: 11%; }

.employee-leave-records__period {
  white-space: nowrap;
}

.employee-leave-records__number {
  text-align: right !important;
}

.employee-leave-records__note {
  text-align: center !important;
}

.employee-leave-records .no-results.employee-leave-records__empty {
  border-color: $ui-line-strong;
  border-style: dashed;
}
</style>
